import { useState, useCallback } from 'react';
import { ScanLine, Keyboard, RotateCcw, QrCode } from 'lucide-react';
import { useTourAutoStart } from '@/hooks/useTourAutoStart';
import { staffScannerTourId, staffScannerSteps } from '@/tours/staff';
import { AnimatePresence, motion } from 'framer-motion';
import { Button, Card } from '@/components/ui';
import QRScanner from '@/components/features/scanner/QRScanner';
import ScanResult from '@/components/features/scanner/ScanResult';
import HandoffConfirmation from '@/components/features/scanner/HandoffConfirmation';
import ManualSearch from '@/components/features/scanner/ManualSearch';
import { pickupApi } from '@/services/api/pickupApi';
import type { QRScanResponse, QRPayload } from '@/services/api/pickupApi';
import toast from 'react-hot-toast';
import { extractErrorMessage } from '@/utils/extractErrorMessage';

const PageState = {
  SCANNING: 'SCANNING',
  SCAN_RESULT: 'SCAN_RESULT',
  HANDOFF: 'HANDOFF',
  MANUAL_SEARCH: 'MANUAL_SEARCH',
} as const;

type PageState = (typeof PageState)[keyof typeof PageState];

const ScannerPage: React.FC = () => {
  useTourAutoStart({ tourId: staffScannerTourId, steps: staffScannerSteps });
  const [pageState, setPageState] = useState<PageState>(PageState.SCANNING);
  const [scanData, setScanData] = useState<QRScanResponse | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const handleQRScan = useCallback(async (decodedText: string) => {
    setIsProcessing(true);

    try {
      let payload: QRPayload;
      try {
        payload = JSON.parse(decodedText) as QRPayload;
      } catch {
        toast.error('Недействительный QR-код');
        setIsProcessing(false);
        return;
      }

      if (!payload.order_id || !payload.ts || !payload.sig) {
        toast.error('Неполный QR-код — отсутствуют обязательные поля');
        setIsProcessing(false);
        return;
      }

      const response = await pickupApi.scanQR({
        order_id: payload.order_id,
        ts: payload.ts,
        sig: payload.sig,
      });

      setScanData(response);
      setPageState(PageState.SCAN_RESULT);
      toast.success(`Заказ №${response.order_number} найден`);
    } catch (err: unknown) {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;

      if (detail) {
        toast.error(detail);
      } else {
        toast.error(extractErrorMessage(err, 'Ошибка проверки QR-кода'));
      }
    } finally {
      setIsProcessing(false);
    }
  }, []);

  const handleProceedToHandoff = () => {
    setPageState(PageState.HANDOFF);
  };

  const handleHandoffComplete = () => {
    setScanData(null);
    setPageState(PageState.SCANNING);
  };

  const handleCancelHandoff = () => {
    setPageState(PageState.SCAN_RESULT);
  };

  const handleReset = () => {
    setScanData(null);
    setPageState(PageState.SCANNING);
  };

  const handleManualOrderSelected = (response: QRScanResponse) => {
    setScanData(response);
    setPageState(PageState.SCAN_RESULT);
  };

  const isScannerActive = pageState === PageState.SCANNING && !isProcessing;

  return (
    <div className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="mb-6 flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2.5 mb-1">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-500 to-emerald-500 flex items-center justify-center">
              <ScanLine className="w-4 h-4 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Сканер</h1>
          </div>
          <p className="text-sm text-gray-500">Сканирование QR-кодов для выдачи заказов</p>
        </div>

        {pageState !== PageState.SCANNING && (
          <Button variant="ghost" size="sm" onClick={handleReset} className="mt-1">
            <RotateCcw className="w-4 h-4" />
            Новое сканирование
          </Button>
        )}
      </div>

      <div className="space-y-5">
        {/* QR Scanner — always rendered to maintain camera, visibility controlled */}
        <div data-tour="staff:scanner-camera" className={pageState !== PageState.MANUAL_SEARCH ? '' : 'hidden'}>
          <QRScanner onScan={handleQRScan} isActive={isScannerActive} />

          {/* Processing overlay text under scanner */}
          {isProcessing && (
            <div className="mt-3 text-center">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-soft border border-gray-100"
              >
                <div className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse" />
                <span className="text-sm font-medium text-gray-600">Проверка QR-кода...</span>
              </motion.div>
            </div>
          )}
        </div>

        {/* Content area — animated transitions */}
        <AnimatePresence mode="wait">
          {/* Scan Result */}
          {pageState === PageState.SCAN_RESULT && scanData && (
            <motion.div
              key="scan-result"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="space-y-4"
            >
              <ScanResult data={scanData} />

              <div className="flex gap-3">
                <Button variant="ghost" className="flex-1" onClick={handleReset}>
                  <RotateCcw className="w-4 h-4" />
                  Сканировать снова
                </Button>
                <Button
                  variant="primary"
                  className="flex-1 !bg-emerald-600 hover:!bg-emerald-700"
                  onClick={handleProceedToHandoff}
                >
                  Подтвердить выдачу
                </Button>
              </div>
            </motion.div>
          )}

          {/* Handoff Confirmation */}
          {pageState === PageState.HANDOFF && scanData && (
            <motion.div
              key="handoff"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <HandoffConfirmation
                scanData={scanData}
                onComplete={handleHandoffComplete}
                onCancel={handleCancelHandoff}
              />
            </motion.div>
          )}

          {/* Manual Search */}
          {pageState === PageState.MANUAL_SEARCH && (
            <motion.div
              key="manual-search"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              data-tour="staff:scanner-manual"
            >
              <ManualSearch
                onOrderSelected={handleManualOrderSelected}
                onClose={handleReset}
              />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Bottom actions — visible during scanning state */}
        {pageState === PageState.SCANNING && !isProcessing && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            <Card padding="sm" className="!bg-gray-50/80 !border-dashed !border-gray-200">
              <button
                onClick={() => setPageState(PageState.MANUAL_SEARCH)}
                className="w-full flex items-center gap-3 py-2 px-1 group"
              >
                <div className="w-10 h-10 rounded-xl bg-white border border-gray-200 flex items-center justify-center group-hover:border-primary-300 group-hover:bg-primary-50 transition-all">
                  <Keyboard className="w-5 h-5 text-gray-400 group-hover:text-primary-500 transition-colors" />
                </div>
                <div className="text-left">
                  <p className="text-sm font-medium text-gray-700 group-hover:text-primary-600 transition-colors">
                    Ввести номер заказа вручную
                  </p>
                  <p className="text-xs text-gray-400">Если QR-код повреждён или отсутствует</p>
                </div>
              </button>
            </Card>
          </motion.div>
        )}

        {/* Tip card */}
        {pageState === PageState.SCANNING && !isProcessing && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
            className="flex items-start gap-3 px-4 py-3 bg-blue-50/60 rounded-xl border border-blue-100"
          >
            <QrCode className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-xs font-medium text-blue-700">Совет</p>
              <p className="text-xs text-blue-600/80 mt-0.5">
                Наведите камеру на QR-код заказа. Сканирование произойдёт автоматически.
                Убедитесь, что код хорошо освещён и находится в рамке.
              </p>
            </div>
          </motion.div>
        )}
      </div>

      {/* Scanline animation keyframes */}
      <style>{`
        @keyframes scanline {
          0%, 100% { transform: translateY(0); opacity: 0; }
          10% { opacity: 1; }
          50% { transform: translateY(250px); opacity: 1; }
          60% { opacity: 0; }
        }
      `}</style>
    </div>
  );
};

export default ScannerPage;
